import { format } from 'date-fns';
import { html } from 'lit';
import { isDiagnosticLog, renderCallDiagnostic, diagnosticEventTypes, renderCallLatency } from './CallDiagnostics.js';

export interface CallLogListEntry {
  timestamp?: Date | string | number | null;
  type?: string | null;
  payload?: string | null;
}

const logTypeColorMap: Record<string, { rowClass: string; badgeClass: string }> = {
  'vad-start': { rowClass: 'border border-warning/30 bg-warning/10', badgeClass: 'badge-warning' },
  'vad-end': { rowClass: 'border border-accent/30 bg-accent/10', badgeClass: 'badge-accent' },
  'vad-real-start': { rowClass: 'border border-secondary/30 bg-secondary/10', badgeClass: 'badge-secondary' },
  transcription: { rowClass: 'border border-primary/30 bg-primary/10', badgeClass: 'badge-primary' },
  'answer-generation-start': { rowClass: 'border border-info/30 bg-info/10', badgeClass: 'badge-info' },
  'answer-generation-end': { rowClass: 'border border-success/30 bg-success/10', badgeClass: 'badge-success' },
  'answer-generation-cancel': { rowClass: 'border border-error/30 bg-error/10', badgeClass: 'badge-error' },
  'relevance-check-start': { rowClass: 'border border-neutral/30 bg-neutral/10', badgeClass: 'badge-neutral' },
  'relevance-check-end': { rowClass: 'border border-success/30 bg-success/10', badgeClass: 'badge-success' },
  'tts-generation-start': { rowClass: 'border border-info/30 bg-info/10', badgeClass: 'badge-info' },
  'tts-generation-end': { rowClass: 'border border-success/30 bg-success/10', badgeClass: 'badge-success' },
  'tts-chunk': { rowClass: 'border border-secondary/30 bg-secondary/10', badgeClass: 'badge-secondary' },
  'tts-start': { rowClass: 'border border-accent/30 bg-accent/10', badgeClass: 'badge-accent' },
  'tts-end': { rowClass: 'border border-neutral/30 bg-neutral/10', badgeClass: 'badge-neutral' },
  'tts-cancel': { rowClass: 'border border-error/30 bg-error/10', badgeClass: 'badge-error' },
  message: { rowClass: 'border border-primary/30 bg-primary/10', badgeClass: 'badge-primary' },
  'message-removed': { rowClass: 'border border-error/30 bg-error/10', badgeClass: 'badge-error' },
  'answer-generation-ttft': { rowClass: 'border border-info/30 bg-info/10', badgeClass: 'badge-info' },
  'tts-time-to-first-token': { rowClass: 'border border-info/30 bg-info/10', badgeClass: 'badge-info' },
};

export const callLogEventTypes = [...Object.keys(logTypeColorMap), ...diagnosticEventTypes];

const getLogTypeClasses = (logType: string) =>
  logTypeColorMap[logType] ?? {
    rowClass: 'border border-base-content/20 bg-base-100',
    badgeClass: 'badge-outline',
  };

/** Serialize call logs to plain text for copy-to-clipboard.
 *  Mirrors the on-screen format: `HH:mm:ss.SSS  [type]  payload` per line. */
export const formatCallLogsAsText = (logs: CallLogListEntry[]): string =>
  logs
    .map(log => {
      const ts = log.timestamp ? format(new Date(log.timestamp), 'HH:mm:ss.SSS') : '??:??:??.???';
      return `${ts}  [${log.type ?? 'unknown'}]  ${log.payload ?? '-'}`;
    })
    .join('\n');

export const renderCallLogList = (logs: CallLogListEntry[]) => {
  if (!logs.length) return html`<p class="text-base-content/50 italic">Keine Protokolle verfügbar.</p>`;

  return html`
    ${renderCallLatency(logs)}
    <ul class="flex flex-col gap-3 w-full">
      ${logs.map(log => {
        const { rowClass, badgeClass } = getLogTypeClasses(log.type ?? '');

        return html`
          <li class="text-base leading-6 wrap-break-word rounded-lg px-4 py-3 ${rowClass}">
            <span class="font-mono text-base-content/70 mr-2">
              ${log.timestamp ? format(new Date(log.timestamp), 'HH:mm:ss.SSS') : '??:??:??.???'}
            </span>
            <span class="badge ${badgeClass} mr-2">${log.type ?? 'unknown'}</span>
            ${isDiagnosticLog(log) ? renderCallDiagnostic(log) : html`<span>${log.payload ?? '-'}</span>`}
          </li>
        `;
      })}
    </ul>
  `;
};
